import {
  applyAction,
  calculateEventLimit,
  getArmyAttackContribution,
  getArmyDefinitionV3ForInstance,
  getCardInstanceDefinition,
  getGarrisonSnapshot,
  getLegalActions,
  getLaneSupplySnapshot,
  getStateHash,
  getTotalAttack,
  getTotalGuard,
  validateAction,
  type GameAction,
  type GameEvent,
  type GameState,
  type LaneId,
} from "@fate-kingdom/engine";
import { create } from "zustand";

import type { WebGlContextStatus } from "../battlefield-3d/ContextLifecycle";
import {
  createFoundationFixtureState,
  type FoundationFixtureMode,
} from "../fixtures/foundationFixtures";
import {
  toArmyDeploymentPreview,
  toCommanderPlacementPreview,
} from "../controller/toMatchViewModel";
import type {
  ArmyDeploymentDiagnosticSnapshot,
  ArmyDeploymentTraceView,
  CommandDiagnosticSnapshot,
  CommandDisbandTraceView,
  CommanderPlacementActionView,
  CommanderPlacementDiagnosticSnapshot,
  CommanderPlacementTraceView,
} from "../presentation/game-view-model";

interface GameStoreState {
  game: GameState;
  fixtureMode: FoundationFixtureMode;
  selectedLaneId: LaneId | undefined;
  selectedCardInstanceId: string | undefined;
  selectedSupplyArmyInstanceId: string | undefined;
  selectedCommandCardInstanceId: string | undefined;
  pendingCommanderActionType: CommanderPlacementActionView | undefined;
  lastCommandDisbandTrace: CommandDisbandTraceView | undefined;
  lastCommanderPlacementTrace: CommanderPlacementTraceView | undefined;
  lastArmyDeploymentTrace: ArmyDeploymentTraceView | undefined;
  lastEvents: readonly GameEvent[];
  validationMessage: string | undefined;
  contextStatus: WebGlContextStatus;
  selectLane: (laneId: LaneId) => void;
  selectCard: (cardInstanceId: string) => void;
  selectSupplyArmy: (armyCardInstanceId: string) => void;
  confirmSupplyDiscard: () => void;
  selectCommandDisbandCard: (cardInstanceId: string) => void;
  confirmCommandDisband: () => void;
  cancelCommandDisband: () => void;
  confirmCommanderPlacement: () => void;
  cancelCommanderPlacement: () => void;
  confirmArmyDeployment: () => void;
  cancelArmyDeployment: () => void;
  cancelSelection: () => void;
  attackSelectedLane: () => void;
  advancePhase: () => void;
  loadFixture: (mode: FoundationFixtureMode) => void;
  setContextStatus: (status: WebGlContextStatus) => void;
  reset: () => void;
}

function commandActionCardInstanceId(action: GameAction): string | undefined {
  if (action.type === "disband-army") return action.armyCardInstanceId;
  if (action.type === "disband-commander") return action.commanderCardInstanceId;
  return undefined;
}

function getCardZone(state: Readonly<GameState>, cardInstanceId: string): string {
  for (const playerId of ["player-1", "player-2"] as const) {
    const player = state.players[playerId];
    if (player.deck.includes(cardInstanceId)) return "deck";
    if (player.hand.includes(cardInstanceId)) return "hand";
    if (player.discard.includes(cardInstanceId)) return "discard";
    if (player.exhaust.includes(cardInstanceId)) return "exhaust";
    if (player.eventResolution.includes(cardInstanceId)) return "event-resolution";
    if (player.activePolicyId === cardInstanceId) return "battlefield-policy";
  }

  for (const lane of Object.values(state.lanes)) {
    for (const playerId of ["player-1", "player-2"] as const) {
      const side = lane.players[playerId];
      if (side.land?.cardInstanceIds.includes(cardInstanceId)) return "battlefield-land";
      if (side.commander?.cardInstanceId === cardInstanceId) return "battlefield-commander";
      if (side.armies.some((army) => army.cardInstanceId === cardInstanceId)) {
        return "battlefield-army";
      }
      if (side.conspiracyCardInstanceId === cardInstanceId) return "battlefield-conspiracy";
      if (side.attachedCardInstanceIds.includes(cardInstanceId)) return "battlefield-attached";
    }
  }
  return "unknown";
}

function createCommandDiagnosticSnapshot(
  state: Readonly<GameState>,
  laneId: LaneId,
  cardInstanceId: string,
): CommandDiagnosticSnapshot {
  const playerId = state.activePlayerId;
  const garrison = getGarrisonSnapshot(state, playerId, laneId);
  return {
    phase: state.phase,
    zone: getCardZone(state, cardInstanceId),
    armyCount: garrison.armyCount,
    totalArmySize: garrison.totalArmySize,
    totalAttack: getTotalAttack(state, playerId, laneId),
    totalGuard: getTotalGuard(state, playerId, laneId),
    eventLimit: calculateEventLimit(state, playerId),
    deploymentActionsUsed: state.players[playerId].actionsUsed.deploymentActions,
    kingdomPool: state.players[playerId].kingdomPool,
    supplyLifecycleStatus: state.schemaVersion === 3 ? state.supplyStatus.type : "legacy",
    randomDraws: state.random.draws,
    randomValue: state.random.value,
    stateHash: getStateHash(state),
  };
}

function createCommanderPlacementDiagnosticSnapshot(
  state: Readonly<GameState>,
  laneId: LaneId,
  incomingCommanderCardInstanceId: string,
  outgoingCommanderCardInstanceId: string | undefined,
): CommanderPlacementDiagnosticSnapshot {
  const playerId = state.activePlayerId;
  const side = state.lanes[laneId].players[playerId];
  const garrison = getGarrisonSnapshot(state, playerId, laneId);
  const battleState: GameState = { ...state, phase: "battle" };
  const attackValidation = validateAction(battleState, {
    type: "attack",
    playerId,
    laneId,
  });
  return {
    incomingZone: getCardZone(state, incomingCommanderCardInstanceId),
    ...(outgoingCommanderCardInstanceId === undefined
      ? {}
      : { outgoingZone: getCardZone(state, outgoingCommanderCardInstanceId) }),
    armyCount: garrison.armyCount,
    totalArmySize: garrison.totalArmySize,
    totalAttack: getTotalAttack(state, playerId, laneId),
    armyAttackContribution: getArmyAttackContribution(state, playerId, laneId),
    totalGuard: getTotalGuard(state, playerId, laneId),
    eventLimit: calculateEventLimit(state, playerId),
    deploymentActionsUsed: state.players[playerId].actionsUsed.deploymentActions,
    kingdomPool: state.players[playerId].kingdomPool,
    eventAuthorizationLanesUsed: state.players[playerId].actionsUsed.eventAuthorizationLanesUsed,
    ...(side.commander === null
      ? {}
      : {
          deployedThisTurn: side.commander.deployedThisTurn,
          mayAttackOnDeploymentTurn: side.commander.mayAttackOnDeploymentTurn,
        }),
    attackEligible: attackValidation.valid,
    ...(attackValidation.valid ? {} : { attackValidationCode: attackValidation.failure.code }),
    supplyLifecycleStatus: state.schemaVersion === 3 ? state.supplyStatus.type : "legacy",
    randomDraws: state.random.draws,
    randomValue: state.random.value,
    stateHash: getStateHash(state),
  };
}

function createArmyDeploymentDiagnosticSnapshot(
  state: Readonly<GameState>,
  laneId: LaneId,
  incomingArmyCardInstanceId: string,
): ArmyDeploymentDiagnosticSnapshot {
  if (state.schemaVersion !== 3) {
    throw new Error("Army deployment diagnostics require schema-V3 state.");
  }
  const playerId = state.activePlayerId;
  const side = state.lanes[laneId].players[playerId];
  const garrison = getGarrisonSnapshot(state, playerId, laneId);
  const laneSupply = getLaneSupplySnapshot(state, laneId);
  return {
    incomingZone: getCardZone(state, incomingArmyCardInstanceId),
    armyCount: garrison.armyCount,
    totalArmySize: garrison.totalArmySize,
    totalAttack: getTotalAttack(state, playerId, laneId),
    armyAttackContribution: getArmyAttackContribution(state, playerId, laneId),
    totalGuard: getTotalGuard(state, playerId, laneId),
    deploymentActionsUsed: state.players[playerId].actionsUsed.deploymentActions,
    kingdomPool: state.players[playerId].kingdomPool,
    laneProduction: laneSupply.production,
    totalUpkeep: laneSupply.upkeep,
    shortfall: laneSupply.shortfall,
    projectedSustainable: laneSupply.sustainable,
    commanderReady: side.commander?.readiness === "ready",
    ...(side.commander === null
      ? {}
      : {
          commanderDeployedThisTurn: side.commander.deployedThisTurn,
          commanderMayAttackOnDeploymentTurn: side.commander.mayAttackOnDeploymentTurn,
        }),
    supplyLifecycleStatus: state.schemaVersion === 3 ? state.supplyStatus.type : "legacy",
    randomDraws: state.random.draws,
    randomValue: state.random.value,
    stateHash: getStateHash(state),
  };
}

function applyEngineAction(
  state: Pick<GameStoreState, "game">,
  action: GameAction,
): Pick<GameStoreState, "game" | "lastEvents" | "validationMessage"> {
  const result = applyAction(state.game, action, {
    rulesVersion: state.game.rulesVersion,
    cardDataVersion: state.game.cardDataVersion,
  });
  if (!result.ok) {
    return {
      game: result.state,
      lastEvents: [],
      validationMessage: `${result.failure.code}: ${result.failure.message}`,
    };
  }

  return {
    game: result.state,
    lastEvents: result.events,
    validationMessage: undefined,
  };
}

function createFixtureState(mode: FoundationFixtureMode) {
  return createFoundationFixtureState(mode);
}

export const useGameStore = create<GameStoreState>((set, get) => ({
  game: createFixtureState("setup-land"),
  fixtureMode: "setup-land",
  selectedLaneId: undefined,
  selectedCardInstanceId: undefined,
  selectedSupplyArmyInstanceId: undefined,
  selectedCommandCardInstanceId: undefined,
  pendingCommanderActionType: undefined,
  lastCommandDisbandTrace: undefined,
  lastCommanderPlacementTrace: undefined,
  lastArmyDeploymentTrace: undefined,
  lastEvents: [],
  validationMessage: undefined,
  contextStatus: "initializing",
  selectLane: (laneId) => {
    const state = get();
    if (state.selectedCardInstanceId === undefined) {
      set({
        selectedLaneId: laneId,
        selectedSupplyArmyInstanceId: undefined,
        selectedCommandCardInstanceId: undefined,
        pendingCommanderActionType: undefined,
        validationMessage: undefined,
      });
      return;
    }

    const selectedDefinition = getCardInstanceDefinition(state.game, state.selectedCardInstanceId);
    if (selectedDefinition?.type === "commander") {
      const actionType: CommanderPlacementActionView =
        state.game.lanes[laneId].players[state.game.activePlayerId].commander === null
          ? "deploy-commander"
          : "replace-commander";
      const preview = toCommanderPlacementPreview(
        state.game,
        state.selectedCardInstanceId,
        laneId,
        actionType,
      );
      if (!preview.legal) {
        set({
          selectedLaneId: undefined,
          pendingCommanderActionType: undefined,
          validationMessage: `Cannot ${actionType === "deploy-commander" ? "deploy" : "replace"}: ${preview.rejectionMessage ?? "That lane is not an engine-legal target."}`,
        });
        return;
      }
      set({
        selectedLaneId: laneId,
        selectedSupplyArmyInstanceId: undefined,
        selectedCommandCardInstanceId: undefined,
        pendingCommanderActionType: actionType,
        validationMessage: undefined,
      });
      return;
    }

    if (selectedDefinition?.type === "army") {
      const preview = toArmyDeploymentPreview(state.game, state.selectedCardInstanceId, laneId);
      if (!preview.legal) {
        set({
          selectedLaneId: undefined,
          pendingCommanderActionType: undefined,
          validationMessage: `Cannot deploy Army: ${preview.rejectionMessage ?? "That lane is not an engine-legal target."}`,
        });
        return;
      }
      set({
        selectedLaneId: laneId,
        selectedSupplyArmyInstanceId: undefined,
        selectedCommandCardInstanceId: undefined,
        pendingCommanderActionType: undefined,
        validationMessage: undefined,
      });
      return;
    }

    const landAction = getLegalActions(state.game, state.game.activePlayerId).find(
      (action) =>
        (action.type === "found-land" || action.type === "develop-land") &&
        action.cardInstanceId === state.selectedCardInstanceId &&
        action.laneId === laneId,
    );

    if (landAction === undefined) {
      set({
        selectedLaneId: laneId,
        validationMessage: "That lane is not an engine-legal target for the selected Land.",
      });
      return;
    }

    set({
      ...applyEngineAction(state, landAction),
      selectedLaneId: laneId,
      selectedCardInstanceId: undefined,
      pendingCommanderActionType: undefined,
    });
  },
  selectCard: (cardInstanceId) => {
    const state = get();
    set({
      selectedCardInstanceId:
        state.selectedCardInstanceId === cardInstanceId ? undefined : cardInstanceId,
      selectedLaneId: undefined,
      selectedSupplyArmyInstanceId: undefined,
      selectedCommandCardInstanceId: undefined,
      pendingCommanderActionType: undefined,
      validationMessage: undefined,
    });
  },
  selectSupplyArmy: (armyCardInstanceId) => {
    const state = get();
    if (state.selectedLaneId === undefined) {
      set({ validationMessage: "Choose an unsustainable lane before selecting an Army." });
      return;
    }

    const legal = getLegalActions(state.game, state.game.activePlayerId).some(
      (action) =>
        action.type === "discard-army-for-upkeep" &&
        action.laneId === state.selectedLaneId &&
        action.armyCardInstanceId === armyCardInstanceId,
    );
    if (!legal) {
      set({ validationMessage: "That Army is not an engine-legal Upkeep discard." });
      return;
    }

    set({
      selectedSupplyArmyInstanceId:
        state.selectedSupplyArmyInstanceId === armyCardInstanceId ? undefined : armyCardInstanceId,
      validationMessage: undefined,
    });
  },
  confirmSupplyDiscard: () => {
    const state = get();
    const action = getLegalActions(state.game, state.game.activePlayerId).find(
      (candidate) =>
        candidate.type === "discard-army-for-upkeep" &&
        candidate.laneId === state.selectedLaneId &&
        candidate.armyCardInstanceId === state.selectedSupplyArmyInstanceId,
    );
    if (action?.type !== "discard-army-for-upkeep") {
      set({ validationMessage: "Select an engine-legal Army before confirming its discard." });
      return;
    }

    const applied = applyEngineAction(state, action);
    const laneStillRequiresDiscard = getLegalActions(
      applied.game,
      applied.game.activePlayerId,
    ).some(
      (candidate) =>
        candidate.type === "discard-army-for-upkeep" && candidate.laneId === action.laneId,
    );
    set({
      ...applied,
      selectedLaneId: laneStillRequiresDiscard ? action.laneId : undefined,
      selectedCardInstanceId: undefined,
      selectedSupplyArmyInstanceId: undefined,
      selectedCommandCardInstanceId: undefined,
      pendingCommanderActionType: undefined,
    });
  },
  selectCommandDisbandCard: (cardInstanceId) => {
    const state = get();
    if (state.selectedLaneId === undefined) {
      set({ validationMessage: "Choose an active lane before selecting a card to disband." });
      return;
    }

    const legal = getLegalActions(state.game, state.game.activePlayerId).some(
      (action) =>
        (action.type === "disband-army" || action.type === "disband-commander") &&
        action.laneId === state.selectedLaneId &&
        commandActionCardInstanceId(action) === cardInstanceId,
    );
    if (!legal) {
      set({ validationMessage: "That card is not an engine-legal voluntary disband choice." });
      return;
    }

    set({
      selectedCommandCardInstanceId:
        state.selectedCommandCardInstanceId === cardInstanceId ? undefined : cardInstanceId,
      selectedCardInstanceId: undefined,
      selectedSupplyArmyInstanceId: undefined,
      pendingCommanderActionType: undefined,
      validationMessage: undefined,
    });
  },
  confirmCommandDisband: () => {
    const state = get();
    const action = getLegalActions(state.game, state.game.activePlayerId).find(
      (candidate) =>
        (candidate.type === "disband-army" || candidate.type === "disband-commander") &&
        candidate.laneId === state.selectedLaneId &&
        commandActionCardInstanceId(candidate) === state.selectedCommandCardInstanceId,
    );
    if (
      (action?.type !== "disband-army" && action?.type !== "disband-commander") ||
      state.selectedLaneId === undefined ||
      state.selectedCommandCardInstanceId === undefined
    ) {
      set({ validationMessage: "Select a current engine-legal card before confirming disband." });
      return;
    }

    const definition = getCardInstanceDefinition(state.game, state.selectedCommandCardInstanceId);
    const before = createCommandDiagnosticSnapshot(
      state.game,
      state.selectedLaneId,
      state.selectedCommandCardInstanceId,
    );
    const applied = applyEngineAction(state, action);
    if (applied.validationMessage !== undefined) {
      set(applied);
      return;
    }
    const after = createCommandDiagnosticSnapshot(
      applied.game,
      state.selectedLaneId,
      state.selectedCommandCardInstanceId,
    );
    const laneStillHasChoices = getLegalActions(applied.game, applied.game.activePlayerId).some(
      (candidate) =>
        (candidate.type === "disband-army" || candidate.type === "disband-commander") &&
        candidate.laneId === state.selectedLaneId,
    );

    set({
      ...applied,
      selectedLaneId: laneStillHasChoices ? state.selectedLaneId : undefined,
      selectedCardInstanceId: undefined,
      selectedSupplyArmyInstanceId: undefined,
      selectedCommandCardInstanceId: undefined,
      pendingCommanderActionType: undefined,
      lastCommandDisbandTrace: {
        actionType: action.type,
        laneId: state.selectedLaneId,
        cardType: action.type === "disband-army" ? "army" : "commander",
        cardName: definition?.name ?? "Unresolved Command card",
        cardInstanceId: state.selectedCommandCardInstanceId,
        cardDefinitionId: definition?.id ?? "unknown",
        before,
        after,
      },
    });
  },
  cancelCommandDisband: () =>
    set({
      selectedLaneId: undefined,
      selectedCommandCardInstanceId: undefined,
      validationMessage: undefined,
    }),
  confirmCommanderPlacement: () => {
    const state = get();
    const laneId = state.selectedLaneId;
    const commanderCardInstanceId = state.selectedCardInstanceId;
    const actionType = state.pendingCommanderActionType;
    if (laneId === undefined || commanderCardInstanceId === undefined || actionType === undefined) {
      set({ validationMessage: "Select a Commander and an engine-legal Land target first." });
      return;
    }

    const action = getLegalActions(state.game, state.game.activePlayerId).find(
      (candidate) =>
        candidate.type === actionType &&
        candidate.laneId === laneId &&
        candidate.commanderCardInstanceId === commanderCardInstanceId,
    );
    if (action?.type !== actionType) {
      const stalePreview = toCommanderPlacementPreview(
        state.game,
        commanderCardInstanceId,
        laneId,
        actionType,
      );
      set({
        validationMessage: `Cannot confirm ${actionType === "deploy-commander" ? "deployment" : "replacement"}: ${stalePreview.rejectionMessage ?? "the selected action is no longer engine-legal."}`,
      });
      return;
    }

    const incomingDefinition = getCardInstanceDefinition(state.game, commanderCardInstanceId);
    if (incomingDefinition?.type !== "commander" || !("cost" in incomingDefinition)) {
      set({ validationMessage: "The selected Commander definition is unavailable." });
      return;
    }
    const outgoingCommanderCardInstanceId =
      state.game.lanes[laneId].players[state.game.activePlayerId].commander?.cardInstanceId;
    const outgoingDefinition =
      outgoingCommanderCardInstanceId === undefined
        ? undefined
        : getCardInstanceDefinition(state.game, outgoingCommanderCardInstanceId);
    const authorizationLaneAlreadyUsed =
      state.game.players[
        state.game.activePlayerId
      ].actionsUsed.eventAuthorizationLanesUsed.includes(laneId);
    const before = createCommanderPlacementDiagnosticSnapshot(
      state.game,
      laneId,
      commanderCardInstanceId,
      outgoingCommanderCardInstanceId,
    );
    const applied = applyEngineAction(state, action);
    if (applied.validationMessage !== undefined) {
      set(applied);
      return;
    }
    const after = createCommanderPlacementDiagnosticSnapshot(
      applied.game,
      laneId,
      commanderCardInstanceId,
      outgoingCommanderCardInstanceId,
    );

    set({
      ...applied,
      selectedLaneId: undefined,
      selectedCardInstanceId: undefined,
      selectedSupplyArmyInstanceId: undefined,
      selectedCommandCardInstanceId: undefined,
      pendingCommanderActionType: undefined,
      lastCommanderPlacementTrace: {
        actionType,
        laneId,
        commanderName: incomingDefinition.name,
        commanderCardInstanceId,
        commanderDefinitionId: incomingDefinition.id,
        ...(outgoingCommanderCardInstanceId === undefined
          ? {}
          : {
              outgoingCommanderName: outgoingDefinition?.name ?? "Unresolved Commander",
              outgoingCommanderCardInstanceId,
              outgoingCommanderDefinitionId: outgoingDefinition?.id ?? "unknown",
              outgoingCommanderDestination: "discard" as const,
            }),
        costPaid: incomingDefinition.cost,
        authorizationLaneAlreadyUsed,
        before,
        after,
      },
    });
  },
  cancelCommanderPlacement: () =>
    set({
      selectedLaneId: undefined,
      pendingCommanderActionType: undefined,
      validationMessage: undefined,
    }),
  confirmArmyDeployment: () => {
    const state = get();
    const laneId = state.selectedLaneId;
    const armyCardInstanceId = state.selectedCardInstanceId;
    if (laneId === undefined || armyCardInstanceId === undefined) {
      set({ validationMessage: "Select an Army and an engine-legal commanded Land target first." });
      return;
    }

    const action = getLegalActions(state.game, state.game.activePlayerId).find(
      (candidate) =>
        candidate.type === "deploy-army" &&
        candidate.laneId === laneId &&
        candidate.armyCardInstanceId === armyCardInstanceId,
    );
    if (action?.type !== "deploy-army") {
      const stalePreview = toArmyDeploymentPreview(state.game, armyCardInstanceId, laneId);
      set({
        validationMessage: `Cannot confirm Army deployment: ${stalePreview.rejectionMessage ?? "the selected action is no longer engine-legal."}`,
      });
      return;
    }

    const definition = getArmyDefinitionV3ForInstance(state.game, armyCardInstanceId);
    if (definition === undefined) {
      set({ validationMessage: "The selected Army definition is unavailable." });
      return;
    }
    const preview = toArmyDeploymentPreview(state.game, armyCardInstanceId, laneId);
    if (!preview.legal || preview.projectedNextSupplySustainable === undefined) {
      set({
        validationMessage: `Cannot confirm Army deployment: ${preview.rejectionMessage ?? "the selected action is no longer engine-legal."}`,
      });
      return;
    }
    const before = createArmyDeploymentDiagnosticSnapshot(state.game, laneId, armyCardInstanceId);
    const applied = applyEngineAction(state, action);
    if (applied.validationMessage !== undefined) {
      set(applied);
      return;
    }
    const after = createArmyDeploymentDiagnosticSnapshot(applied.game, laneId, armyCardInstanceId);

    set({
      ...applied,
      selectedLaneId: undefined,
      selectedCardInstanceId: undefined,
      selectedSupplyArmyInstanceId: undefined,
      selectedCommandCardInstanceId: undefined,
      pendingCommanderActionType: undefined,
      lastArmyDeploymentTrace: {
        actionType: "deploy-army",
        laneId,
        armyName: definition.name,
        armyCardInstanceId,
        armyDefinitionId: definition.id,
        costPaid: definition.cost,
        printedUpkeep: definition.upkeep,
        armySize: definition.armySize,
        printedAttack: definition.attack,
        printedGuard: definition.guard,
        projectedNextSupplySustainable: preview.projectedNextSupplySustainable,
        before,
        after,
      },
    });
  },
  cancelArmyDeployment: () =>
    set({
      selectedLaneId: undefined,
      pendingCommanderActionType: undefined,
      validationMessage: undefined,
    }),
  cancelSelection: () =>
    set({
      selectedCardInstanceId: undefined,
      selectedSupplyArmyInstanceId: undefined,
      selectedCommandCardInstanceId: undefined,
      pendingCommanderActionType: undefined,
      selectedLaneId: undefined,
      validationMessage: undefined,
    }),
  attackSelectedLane: () => {
    const state = get();
    if (state.selectedLaneId === undefined) {
      set({ validationMessage: "Select a lane before confirming an attack." });
      return;
    }

    set(
      applyEngineAction(state, {
        type: "attack",
        playerId: state.game.activePlayerId,
        laneId: state.selectedLaneId,
      }),
    );
  },
  advancePhase: () => {
    const state = get();
    set({
      ...applyEngineAction(state, {
        type: "advance-phase",
        playerId: state.game.activePlayerId,
      }),
      selectedCardInstanceId: undefined,
      selectedSupplyArmyInstanceId: undefined,
      selectedCommandCardInstanceId: undefined,
      pendingCommanderActionType: undefined,
      selectedLaneId: undefined,
    });
  },
  loadFixture: (fixtureMode) =>
    set({
      game: createFixtureState(fixtureMode),
      fixtureMode,
      selectedLaneId: undefined,
      selectedCardInstanceId: undefined,
      selectedSupplyArmyInstanceId: undefined,
      selectedCommandCardInstanceId: undefined,
      pendingCommanderActionType: undefined,
      lastCommandDisbandTrace: undefined,
      lastCommanderPlacementTrace: undefined,
      lastArmyDeploymentTrace: undefined,
      lastEvents: [],
      validationMessage: undefined,
    }),
  setContextStatus: (contextStatus) => set({ contextStatus }),
  reset: () => {
    const fixtureMode = get().fixtureMode;
    set({
      game: createFixtureState(fixtureMode),
      selectedLaneId: undefined,
      selectedCardInstanceId: undefined,
      selectedSupplyArmyInstanceId: undefined,
      selectedCommandCardInstanceId: undefined,
      pendingCommanderActionType: undefined,
      lastCommandDisbandTrace: undefined,
      lastCommanderPlacementTrace: undefined,
      lastArmyDeploymentTrace: undefined,
      lastEvents: [],
      validationMessage: undefined,
    });
  },
}));
